import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faFileDownload, faFileExcel, faGripVertical, faRotateLeft, faStar, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { Button, Form, Offcanvas, Spinner } from 'react-bootstrap';
import { DragEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../../store/user';
import {
    applyFavoriteColumnOrder,
    getDefaultMappedColumnIds,
    moveColumnInOrder,
    prioritizeSelectedColumnOrder,
    sanitizeGuidColumnOrder,
    uniqueCatalogIds
} from '../../helpers/exportOrderColumns';
import {
    createExportView,
    CreateExportViewColumn,
    deleteExportView,
    ExportView,
    ExportViewColumnDefinition,
    getExportViewColumns,
    getExportViews,
    setDefaultExportView
} from '../../services/exportViewService';
import UniModal from '../UniModal/UniModal';
import './UniExportOrders.scss';



export interface ExportOrdersSelection {
    columns: CreateExportViewColumn[];
    exportViewId?: string;
}

type StoredExportConfig = {
    lastColumns: string[];
    columnOrder: string[];
    lastViewId: string;
};

interface UniExportOrdersProps {
    show: boolean;
    onHide: () => void;
    selectedCount: number;
    loading?: boolean;
    reportType?: number;
    onExport: (selection: ExportOrdersSelection) => void;
}

const storageKey = (userId: string, customerId: string, reportType: number) => `unipar-export-orders:${userId}:${customerId}:${reportType}`;
const favoriteNameAllowedCharacters = /^[\p{L}\p{N} ]+$/u;
const sanitizeFavoriteName = (value: string) => value.replace(/[^\p{L}\p{N} ]/gu, '');

const readStoredConfig = (userId: string, customerId: string, reportType: number): StoredExportConfig => {
    try {
        const raw = localStorage.getItem(storageKey(userId, customerId, reportType));
        if (!raw) {
            return { lastColumns: [], columnOrder: [], lastViewId: '' };
        }

        const parsed = JSON.parse(raw) as StoredExportConfig;
        return {
            lastColumns: Array.isArray(parsed.lastColumns) ? parsed.lastColumns : [],
            columnOrder: Array.isArray(parsed.columnOrder) ? parsed.columnOrder : [],
            lastViewId: typeof parsed.lastViewId === 'string' ? parsed.lastViewId : ''
        };
    } catch {
        return { lastColumns: [], columnOrder: [], lastViewId: '' };
    }
};

const writeStoredConfig = (userId: string, customerId: string, reportType: number, config: StoredExportConfig) => {
    if (!userId || !customerId) {
        return;
    }

    try {
        localStorage.setItem(storageKey(userId, customerId, reportType), JSON.stringify(config));
    } catch {
        // Ignore storage quota / private mode failures
    }
};

const pickDefaultExportView = (views: ExportView[]): ExportView | undefined => {
    const defaults = views.filter(view => view.isDefault);
    if (!defaults.length) {
        return undefined;
    }

    return [...defaults].sort((a, b) => {
        const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
        const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
        return bTime - aTime;
    })[0];
};

    const HandleColumnName = (column: ExportViewColumnDefinition) => {
        const { t, i18n } = useTranslation();
        const lang = i18n.language.toLowerCase();

        if (lang == 'pt') {
            return column.namePt;
        } else if (lang == 'es') {
            return column.nameEs;
        } else {
            return column.nameEn;
        }
    }

const UniExportOrders = ({ show, onHide, selectedCount, loading, reportType = 1, onExport }: UniExportOrdersProps) => {
    const { t, i18n } = useTranslation();
    const { darkMode, loginData, selectedCustomer } = useUserStore();
    const [catalog, setCatalog] = useState<ExportViewColumnDefinition[]>([]);
    const [columnOrder, setColumnOrder] = useState<string[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [favorites, setFavorites] = useState<ExportView[]>([]);
    const [favoriteName, setFavoriteName] = useState('');
    const [activeFavorite, setActiveFavorite] = useState('');
    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [dropTargetId, setDropTargetId] = useState<string | null>(null);
    const [loadingCatalog, setLoadingCatalog] = useState(false);
    const [savingFavorite, setSavingFavorite] = useState(false);
    const [catalogError, setCatalogError] = useState('');
    const [viewsError, setViewsError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [favoriteToDelete, setFavoriteToDelete] = useState<ExportView | null>(null);
    const [deletingFavorite, setDeletingFavorite] = useState(false);
    const [settingDefaultId, setSettingDefaultId] = useState('');

    const columnsById = useMemo(() => {
        const map = new Map<string, ExportViewColumnDefinition>();
        catalog.forEach(column => map.set(column.id, column));
        return map;
    }, [catalog]);

    const selectedColumns = useMemo(
        () => columnOrder.filter(id => selectedIds.includes(id)),
        [columnOrder, selectedIds]
    );

    const selectedColumnDefs = useMemo(
        () => selectedColumns.map(id => columnsById.get(id)).filter((column): column is ExportViewColumnDefinition => !!column),
        [selectedColumns, columnsById]
    );

    const persist = (nextOrder: string[], nextSelected = selectedIds, nextViewId = activeFavorite) => {
        writeStoredConfig((loginData.id || process.env.REACT_APP_ACCESSAS_USER_ID) ?? '', selectedCustomer.customerId, reportType, {
            lastColumns: nextOrder.filter(id => nextSelected.includes(id)),
            columnOrder: nextOrder,
            lastViewId: nextViewId || ''
        });
    };

    const resolveApiError = (
        response: { error?: any; errorPT?: string; errorEN?: string; errorES?: string },
        fallbackKey: string
    ) => {
        const lang = i18n.language.toLowerCase();
        const fallback = t(fallbackKey);
        if (lang.startsWith('pt')) {
            return response.errorPT || (typeof response.error === 'string' ? response.error : fallback);
        }
        if (lang.startsWith('es')) {
            return response.errorES || (typeof response.error === 'string' ? response.error : fallback);
        }
        return response.errorEN || (typeof response.error === 'string' ? response.error : fallback);
    };

    const applyView = (view: ExportView, catalogIds: string[], persistSelection = true) => {
        const favoriteIds = view.columns.map(column => column.columnId);
        const nextOrder = applyFavoriteColumnOrder(favoriteIds, catalogIds);
        const nextSelected = uniqueCatalogIds(favoriteIds, catalogIds);
        setColumnOrder(nextOrder);
        setSelectedIds(nextSelected);
        setActiveFavorite(view.id);
        if (persistSelection) {
            persist(nextOrder, nextSelected, view.id);
        }
    };

    const loadPanelData = async () => {
        const [columnsResponse, viewsResponse] = await Promise.all([
            getExportViewColumns(reportType),
            getExportViews((loginData.id || process.env.REACT_APP_ACCESSAS_USER_ID) ?? '', selectedCustomer.customerId, undefined, reportType)
        ]);

        return { columnsResponse, viewsResponse };
    };

    useEffect(() => {
        if (!show) {
            return;
        }

        let cancelled = false;
        const stored = readStoredConfig((loginData.id || process.env.REACT_APP_ACCESSAS_USER_ID)?? '', selectedCustomer.customerId, reportType);
        setFavoriteName('');
        setActiveFavorite('');
        setDraggingId(null);
        setDropTargetId(null);
        setSaveError('');
        setViewsError('');
        setCatalogError('');
        setFavoriteToDelete(null);
        setDeletingFavorite(false);
        setLoadingCatalog(true);

        loadPanelData()
            .then(({ columnsResponse, viewsResponse }) => {
                if (cancelled) {
                    return;
                }

                if (!columnsResponse.success) {
                    setCatalog([]);
                    setColumnOrder([]);
                    setSelectedIds([]);
                    setFavorites([]);
                    setCatalogError(resolveApiError(columnsResponse, 'myOrders.loadExportColumnsError'));
                    return;
                }

                const nextCatalog = columnsResponse.data;
                const catalogIds = nextCatalog.map(column => column.id);
                setCatalog(nextCatalog);
                setCatalogError('');

                let nextViews: ExportView[] = [];
                if (viewsResponse.success) {
                    nextViews = viewsResponse.data;
                    setViewsError('');
                } else {
                    setViewsError(resolveApiError(viewsResponse, 'myOrders.loadExportViewsError'));
                }
                setFavorites(nextViews);

                const storedView = stored.lastViewId
                    ? nextViews.find(view => view.id === stored.lastViewId)
                    : undefined;
                const defaultView = pickDefaultExportView(nextViews) ?? storedView;
                if (defaultView) {
                    applyView(defaultView, catalogIds, true);
                    return;
                }

                const nextOrder = sanitizeGuidColumnOrder(stored.columnOrder, catalogIds);
                const nextSelected = uniqueCatalogIds(stored.lastColumns, catalogIds);
                const prioritizedOrder = prioritizeSelectedColumnOrder(nextOrder, nextSelected);
                setColumnOrder(prioritizedOrder);
                setSelectedIds(nextSelected);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingCatalog(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [show, loginData.id, selectedCustomer.customerId, reportType]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleColumn = (id: string) => {
        setActiveFavorite('');
        const nextSelected = selectedIds.includes(id)
            ? selectedIds.filter(column => column !== id)
            : [...selectedIds, id];
        const nextOrder = prioritizeSelectedColumnOrder(columnOrder, nextSelected);
        setColumnOrder(nextOrder);
        setSelectedIds(nextSelected);
        persist(nextOrder, nextSelected, '');
    };

    const applyOrder = (nextOrder: string[]) => {
        const prioritizedOrder = prioritizeSelectedColumnOrder(nextOrder, selectedIds);
        setActiveFavorite('');
        setColumnOrder(prioritizedOrder);
        persist(prioritizedOrder, selectedIds, '');
    };

    const moveColumn = (id: string, direction: 'up' | 'down') => {
        const index = columnOrder.indexOf(id);
        if (index < 0) {
            return;
        }

        const swapWith = direction === 'up' ? index - 1 : index + 1;
        if (swapWith < 0 || swapWith >= columnOrder.length) {
            return;
        }

        applyOrder(moveColumnInOrder(columnOrder, id, columnOrder[swapWith]));
    };

    const moveColumnTo = (fromId: string, toId: string) => {
        applyOrder(moveColumnInOrder(columnOrder, fromId, toId));
    };

    const selectAll = () => {
        setActiveFavorite('');
        const next = catalog.map(column => column.id);
        setSelectedIds(next);
        persist(columnOrder, next, '');
    };

    const clearAll = () => {
        setActiveFavorite('');
        setSelectedIds([]);
        persist(columnOrder, [], '');
    };

    const restoreDefault = () => {
        const catalogIds = catalog.map(column => column.id);
        const defaultView = pickDefaultExportView(favorites);
        if (defaultView) {
            applyView(defaultView, catalogIds);
            return;
        }

        setActiveFavorite('');
        const nextSelected = getDefaultMappedColumnIds(catalog);
        const nextOrder = prioritizeSelectedColumnOrder(catalogIds, nextSelected);
        setColumnOrder(nextOrder);
        setSelectedIds(nextSelected);
        persist(nextOrder, nextSelected, '');
    };

    const refreshViews = async (activeName?: string, exportViewId?: string) => {
        const viewsResponse = await getExportViews((loginData.id || process.env.REACT_APP_ACCESSAS_USER_ID) ?? '', selectedCustomer.customerId, exportViewId, reportType);
        if (!viewsResponse.success) {
            setViewsError(resolveApiError(viewsResponse, 'myOrders.loadExportViewsError'));
            return;
        }

        setViewsError('');
        setFavorites(viewsResponse.data);
        if (activeName) {
            const match = viewsResponse.data
                .filter(view => view.name.toLowerCase() === activeName.toLowerCase())
                .sort((a, b) => Date.parse(b.createdAt || '') - Date.parse(a.createdAt || ''))[0];
            setActiveFavorite(match?.id ?? '');
            persist(columnOrder, selectedIds, match?.id ?? '');
        }
    };

    const saveFavorite = async () => {
        const name = favoriteName.trim();
        if (!name || !favoriteNameAllowedCharacters.test(name) || selectedColumnDefs.length === 0 || savingFavorite || loadingCatalog) {
            return;
        }


        setSavingFavorite(true);
        setSaveError('');
        const userEntityId = (loginData.id || process.env.REACT_APP_ACCESSAS_USER_ID) ?? '';
        
        try {
            const response = await createExportView({
                userEntityId: userEntityId,
                customerId: selectedCustomer.customerId,
                reportType,
                name,
                isDefault: false,
                columns: selectedColumnDefs.map((column, index) => ({
                    displayNamePt: column.namePt,
                    displayNameEs: column.nameEs,
                    displayNameEn: column.nameEn,
                    sortOrder: index + 1,
                    columnId: column.id
                }))
            });

            if (!response.success) {
                setSaveError(resolveApiError(response, 'myOrders.saveFavoriteError'));
                return;
            }

            setFavoriteName('');
            await refreshViews(name, activeFavorite || undefined);
        } finally {
            setSavingFavorite(false);
        }
    };

    const applyFavorite = async (favorite: ExportView) => {
        if (favorite.columns?.length) {
            setViewsError('');
            applyView(favorite, catalog.map(column => column.id));
            return;
        }

        const viewsResponse = await getExportViews(
            (loginData.id || process.env.REACT_APP_ACCESSAS_USER_ID) ?? '',
            selectedCustomer.customerId,
            favorite.id,
            reportType
        );

        if (!viewsResponse.success) {
            setViewsError(resolveApiError(viewsResponse, 'myOrders.loadExportViewsError'));
            return;
        }

        setViewsError('');
        const selectedView = viewsResponse.data.find(view => view.id === favorite.id) ?? favorite;
        setFavorites(currentFavorites => currentFavorites.map(view => (
            view.id === selectedView.id ? selectedView : view
        )));
        applyView(selectedView, catalog.map(column => column.id));
    };

    const setDefaultFavorite = async (favorite: ExportView) => {
        if (favorite.isDefault || settingDefaultId) {
            return;
        }

        setSettingDefaultId(favorite.id);
        setSaveError('');
        try {
            const response = await setDefaultExportView(favorite.id);
            if (!response.success) {
                setSaveError(resolveApiError(response, 'myOrders.setDefaultFavoriteError'));
                return;
            }

            await applyFavorite(favorite);
            setFavorites(currentFavorites => currentFavorites.map(view => ({
                ...view,
                isDefault: view.id === favorite.id
            })));
        } finally {
            setSettingDefaultId('');
        }
    };

    const requestDeleteFavorite = (favorite: ExportView) => {
        setSaveError('');
        setFavoriteToDelete(favorite);
    };

    const closeDeleteFavorite = () => {
        if (deletingFavorite) {
            return;
        }
        setFavoriteToDelete(null);
    };

    const confirmDeleteFavorite = async () => {
        if (!favoriteToDelete || deletingFavorite) {
            return;
        }

        setDeletingFavorite(true);
        setSaveError('');

        try {
            const response = await deleteExportView(favoriteToDelete.id);
            if (!response.success) {
                setSaveError(resolveApiError(response, 'myOrders.deleteFavoriteError'));
                return;
            }

            const deletedId = favoriteToDelete.id;
            const remainingViewId = activeFavorite === deletedId ? '' : activeFavorite;
            setFavoriteToDelete(null);
            if (!remainingViewId) {
                setActiveFavorite('');
                persist(columnOrder, selectedIds, '');
            }
            await refreshViews(undefined, remainingViewId || undefined);
        } finally {
            setDeletingFavorite(false);
        }
    };

    const isInteractiveDragTarget = (target: EventTarget | null) => {
        return (target as HTMLElement | null)?.closest('button, input, .form-check') != null;
    };

    const handleDragStart = (event: DragEvent<HTMLDivElement>, id: string) => {
        if (isInteractiveDragTarget(event.target)) {
            event.preventDefault();
            return;
        }

        event.dataTransfer.setData('text/plain', id);
        event.dataTransfer.effectAllowed = 'move';
        setDraggingId(id);
        setDropTargetId(null);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>, id: string) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (dropTargetId !== id) {
            setDropTargetId(id);
        }
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>, id: string) => {
        event.preventDefault();
        const fromId = event.dataTransfer.getData('text/plain');
        if (fromId && columnOrder.includes(fromId)) {
            moveColumnTo(fromId, id);
        }
        setDraggingId(null);
        setDropTargetId(null);
    };

    const handleDragEnd = () => {
        setDraggingId(null);
        setDropTargetId(null);
    };

    const HandleColumnName = (column: ExportViewColumnDefinition) => {

        const lang = i18n.language.toLowerCase();

        if (lang == 'pt') {
            return column.namePt;
        } else if (lang == 'es') {
            return column.nameEs;
        } else {
            return column.nameEn;
        }
    }

    const catalogReady = !loadingCatalog && catalog.length > 0 && !catalogError;
    const canExport = selectedColumnDefs.length > 0 && !loading && catalogReady;
    const favoriteNameIsValid = !!favoriteName.trim() && favoriteNameAllowedCharacters.test(favoriteName.trim());
    const canSaveFavorite = favoriteNameIsValid && selectedColumnDefs.length > 0 && catalogReady && !savingFavorite;

    return (
        <>
        <Offcanvas
            show={show}
            onHide={() => {
                setFavoriteToDelete(null);
                onHide();
            }}
            placement='end'
            className={`uni-offcanvas-export-orders${darkMode ? ' dark' : ''}`}
            backdropClassName='uni-offcanvas-export-orders-backdrop'
            enforceFocus={!favoriteToDelete}
        >
            <Offcanvas.Body>
                <div className='export-panel'>
                    <div className='export-panel-header'>
                        <div className='export-panel-title-wrap'>
                            <div className='export-panel-icon'>
                                <FontAwesomeIcon icon={faFileExcel} />
                            </div>
                            <div>
                                <h2 className='export-panel-title'>{t('myOrders.configureExport')}</h2>
                                <p className='export-panel-subtitle'>{t('myOrders.configureExportSubtitle')}</p>
                            </div>
                        </div>
                        <button type='button' className='export-panel-close' onClick={onHide} aria-label={t('myOrders.close')}>
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </div>

                    <div className='export-panel-favorites'>
                        <div className='export-panel-favorites-title'>
                            <FontAwesomeIcon icon={faStar} />
                            {t('myOrders.favoriteConfigs')}
                        </div>
                        <div className='export-panel-favorites-row'>
                            <input
                                className='export-panel-favorite-input'
                                value={favoriteName}
                                onChange={(e) => {
                                    setFavoriteName(sanitizeFavoriteName(e.target.value));
                                    if (saveError) {
                                        setSaveError('');
                                    }
                                }}
                                placeholder={t('myOrders.favoriteNamePlaceholder')}
                                maxLength={30}
                                disabled={!catalogReady || savingFavorite}
                                title={t('myOrders.specialCharacters')}
                            />
                            <button
                                type='button'
                                className='export-panel-save-favorite'
                                onClick={saveFavorite}
                                disabled={!canSaveFavorite}
                            >
                                {savingFavorite ? <Spinner animation='border' /> : t('myOrders.saveFavorite')}
                            </button>
                        </div>
                        {(saveError || viewsError) && (
                            <div className='export-panel-error'>{saveError || viewsError}</div>
                        )}
                        {favorites.length > 0 && (
                            <div className='export-panel-favorite-list'>
                                {favorites.map(favorite => (
                                    <div
                                        key={favorite.id}
                                        className={`export-panel-favorite-chip${activeFavorite === favorite.id ? ' active' : ''}`}
                                    >
                                        <button
                                            type='button'
                                            className={`export-panel-favorite-default${favorite.isDefault ? ' is-default' : ''}`}
                                            onClick={() => setDefaultFavorite(favorite)}
                                            disabled={!!settingDefaultId || favorite.isDefault}
                                            aria-label={t('myOrders.setDefaultFavorite')}
                                            title={favorite.isDefault ? 'PADRÃO' : t('myOrders.setDefaultFavorite')}
                                        >
                                            <FontAwesomeIcon icon={faStar} />
                                        </button>
                                        <button
                                            type='button'
                                            className='export-panel-favorite-chip-name'
                                            onClick={() => applyFavorite(favorite)}
                                        >
                                            {favorite.name}
                                        </button>
                                        {favorite.isDefault && <span className='export-panel-favorite-default-label'>| {t('myOrders.exportViewDefault')}</span>}
                                        <button
                                            type='button'
                                            className='export-panel-favorite-remove'
                                            onClick={() => requestDeleteFavorite(favorite)}
                                            disabled={deletingFavorite}
                                            aria-label={t('myOrders.deleteFavorite')}
                                            title={t('myOrders.deleteFavorite')}
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className='export-panel-toolbar'>
                        <div className='export-panel-hint'>{t('myOrders.reorderHint')}</div>
                        <div className='export-panel-actions'>
                            <button type='button' className='export-panel-action' onClick={selectAll} disabled={!catalogReady}>
                                {t('myOrders.selectAllColumns')}
                            </button>
                            <button type='button' className='export-panel-action' onClick={clearAll} disabled={!catalogReady}>
                                {t('myOrders.clearColumns')}
                            </button>
                            {/* <button type='button' className='export-panel-action restore' onClick={restoreDefault} disabled={!catalogReady} >
                                <FontAwesomeIcon icon={faRotateLeft} />
                                {t('myOrders.restoreDefaultColumns')}
                            </button> */}
                            <span className='export-panel-count'>
                                {t('myOrders.columnsCount', { selected: selectedColumns.length, total: catalog.length })}
                            </span>
                        </div>
                    </div>

                    <div className='export-panel-list'>
                        {loadingCatalog && (
                            <div className='export-panel-loading'>
                                <Spinner animation='border' />
                            </div>
                        )}
                        {!loadingCatalog && catalogError && (
                            <div className='export-panel-error'>{catalogError}</div>
                        )}
                        {!loadingCatalog && !catalogError && columnOrder.map((id, index) => {
                            const column = columnsById.get(id);
                            if (!column) {
                                return null;
                            }

                            const selectedIndex = selectedColumns.indexOf(id);
                            const isSelected = selectedIndex >= 0;

                            return (
                                <div
                                    key={id}
                                    className={`export-panel-row${isSelected ? ' selected' : ''}${draggingId === id ? ' dragging' : ''}${dropTargetId === id && draggingId && draggingId !== id ? ' drop-target' : ''}`}
                                    draggable
                                    onDragStart={(event) => handleDragStart(event, id)}
                                    onDragOver={(event) => handleDragOver(event, id)}
                                    onDrop={(event) => handleDrop(event, id)}
                                    onDragEnd={handleDragEnd}
                                >
                                    <Form.Check
                                        type='checkbox'
                                        checked={isSelected}
                                        onChange={() => toggleColumn(id)}
                                    />
                                    <span className='export-panel-grip' title={t('myOrders.reorderHint')}>
                                        <FontAwesomeIcon icon={faGripVertical} />
                                    </span>
                                    <span className='export-panel-row-label'>{HandleColumnName(column)}</span>
                                    {isSelected && (
                                        <span className='export-panel-row-index'>{selectedIndex + 1}</span>
                                    )}
                                    <div className='export-panel-arrows'>
                                        <button
                                            type='button'
                                            className='export-panel-arrow'
                                            onClick={() => moveColumn(id, 'up')}
                                            disabled={index === 0}
                                            aria-label={t('myOrders.moveColumnUp')}
                                        >
                                            <FontAwesomeIcon icon={faChevronUp} />
                                        </button>
                                        <button
                                            type='button'
                                            className='export-panel-arrow'
                                            onClick={() => moveColumn(id, 'down')}
                                            disabled={index === columnOrder.length - 1}
                                            aria-label={t('myOrders.moveColumnDown')}
                                        >
                                            <FontAwesomeIcon icon={faChevronDown} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className='export-panel-footer'>
                        <span className='export-panel-orders-count'>
                            {t(selectedCount === 1 ? 'myOrders.ordersCountOne' : 'myOrders.ordersCount', { count: selectedCount })}
                        </span>
                        <button type='button' className='export-panel-cancel' onClick={onHide}>
                            {t('myOrders.cancel')}
                        </button>
                        <button
                            type='button'
                            className='export-panel-submit'
                            disabled={!canExport}
                            onClick={() => onExport({
                                columns: selectedColumnDefs.map((column, index) => ({
                                    displayNamePt: column.namePt,
                                    displayNameEs: column.nameEs,
                                    displayNameEn: column.nameEn,
                                    sortOrder: index + 1,
                                    columnId: column.id
                                })),
                                exportViewId: activeFavorite || undefined
                            })}
                        >
                            {loading ? <Spinner animation='border' /> : <FontAwesomeIcon icon={faFileDownload} />}
                            {t('myOrders.exportSpreadsheet')}
                        </button>
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
        <UniModal
            icon={faTrashAlt}
            isFontAwsome
            title={t('myOrders.deleteFavorite')}
            message=''
            iconColor='#EA2027'
            show={!!favoriteToDelete}
            onHide={closeDeleteFavorite}
            className='export-view-delete-modal'
            backdropClassName='export-view-delete-modal-backdrop'
            iconWidth={70}
            iconHeight={70}
        >
            <p>{t('myOrders.deleteFavoriteConfirm', { name: favoriteToDelete?.name ?? '' })}</p>
            {saveError && <p className='export-view-delete-error'>{saveError}</p>}
            {deletingFavorite ?
                (<Spinner style={{ color: '#027948', width: 25, height: 25 }} />)
                :
                (
                    <>
                        <Button variant='primary' style={{ backgroundColor: '#FF0000' }} onClick={confirmDeleteFavorite}>{t('myOrders.yes')}</Button>&nbsp;
                        <Button variant='primary' onClick={closeDeleteFavorite}>{t('myOrders.no')}</Button>
                    </>
                )
            }
        </UniModal>
        </>
    );
};

export default UniExportOrders;
